import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Exceptions() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { user } = useAuth();
  const nav = useNavigate();
  const isMgr = user?.role === "manager";

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (filter !== "all") q.set("status", filter);
    if (dateRange?.from) q.set("date_from", dateRange.from);
    if (dateRange?.to) q.set("date_to", dateRange.to);
    q.set("limit", String(pageSize));
    q.set("skip", String((page - 1) * pageSize));
    const { data } = await api.get(`/exceptions?${q.toString()}`);
    setItems(data.items);
    setTotal(data.total || data.items.length);
  }, [filter, dateRange, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, dateRange, pageSize]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/exceptions/${id}/decide`, { decision, note: "" });
      toast.success(`Exception ${decision}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Exception Center" subtitle="Review and act on flagged work">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="exception-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <DateRangePicker testId="exceptions-date-range" value={dateRange} onChange={setDateRange} />
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState title="No exceptions" subtitle="All work is on track." />
      ) : (
        <div className="border rounded-md bg-card">
          <div className="divide-y">
            {items.map(e => (
              <div key={e.id} data-testid={`exception-${e.id}`} className="p-5 flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className={`w-10 h-10 grid place-items-center rounded-md ${e.status === "pending" ? "bg-amber-100 text-amber-700" : e.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                    <Warning size={20} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{e.type.replace(/_/g, " ")}</span>
                      <span className="text-xs mono text-muted-foreground">· case <button onClick={() => nav(`/cases/${e.case_id}`)} className="text-primary hover:underline">{e.case_ref || e.case_id.slice(0, 8)}</button></span>
                      <span className="text-xs text-muted-foreground">by {e.raised_by_name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{e.reason}</div>
                    {e.customer && <div className="text-xs mono text-muted-foreground mt-1">Customer: {e.customer}</div>}
                    <div className="text-[11px] mono text-muted-foreground mt-1">{formatDate(e.at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                    e.status === "approved" ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                    e.status === "rejected" ? "border-rose-300 text-rose-700 bg-rose-50" :
                    "border-amber-300 text-amber-700 bg-amber-50"
                  }`}>{e.status}</span>
                  {isMgr && e.status === "pending" && (
                    <>
                      <Button data-testid={`approve-${e.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(e.id, "approve")}>
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                      <Button data-testid={`reject-${e.id}`} size="sm" variant="outline" onClick={() => decide(e.id, "reject")}>
                        <X size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Pagination testId="exceptions-pagination" page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </div>
      )}
    </div>
  );
}
